import { useContext, useEffect, useState } from 'react';
import { getData } from '../backend';
import { useLocation } from 'react-router-dom';
import { SectionsContext } from '../contexts';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import packageJson from '../../package.json';
import useUser from '../hooks/useUser';
import { initials, luminance } from '../utils';
import SidebarItem from './SidebarItem';

function Sidebar() {
  const { t } = useTranslation();

  const { user } = useUser();

  const pathname = useLocation().pathname;

  const { data: apiVersionData } = useSWR("/version/", getData);

  const { workflow, template, settings } = useContext(SectionsContext);

  const { workflowUrl: lastWorkflowUrl } = workflow;
  const { templateUrl: lastTemplateUrl } = template;
  const { settingsUrl: lastSettingsUrl } = settings;

  const workflowUrl = (pathname.includes('/w/') || pathname === "/workflows") ? '/workflows' : lastWorkflowUrl;
  const templateUrl = pathname.includes('/templates') ? '/templates' : lastTemplateUrl;
  const settingsUrl = pathname.includes('/settings') ? '/settings' : lastSettingsUrl;

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(!pathname.startsWith("/w/"));
  }, [pathname]);

  return (
    <nav key={"sidebar"} className={`flex flex-col bg-white overflow-x-visible border-r border-gray-200 z-20 gap-4 transition-width ${isExpanded ? "w-60" : "w-18"}`}>
      <div className='flex items-center gap-2 px-4 w-full h-14'>
        <div className='w-[36px] h-[36px] rounded-md bg-blue flex items-center justify-center font-semibold text-white text-lg cursor-pointer' style={{ "backgroundColor": user?.activeTeam?.brandingColour }} onClick={() => setIsExpanded(f => !f)}>
          {initials(user?.activeTeam?.name || user?.fullName || "").slice(0, 2)}
        </div>
        {isExpanded &&
          <div className='flex flex-col mt-1'>
            <div className='text-xs font-semibold text-gray-600 leading-none mb-1'>{!!user?.activeTeam ? "Workspace" : "Welcome"} </div>
            <div className='font-semibold font-gilroy text-gray-900 truncate w-40 leading-none' style={{ "color": luminance(user?.activeTeam?.brandingColour ?? "#fff") > 0.5 ? "" : user?.activeTeam?.brandingColour }}>
              {user?.activeTeam?.name || user?.fullName}
            </div>
          </div>
        }
      </div>
      <div className='flex flex-col gap-2 mt-2'>
        <SidebarItem
          name={t('Explore')}
          icon="GlobeIcon"
          href={templateUrl}
          isExpanded={isExpanded}
          isSelected={pathname.startsWith("/templates")}
        />
        <SidebarItem
          name={t('Home')}
          icon="HomeIcon"
          href={"/"}
          isExpanded={isExpanded}
          isSelected={pathname === "/"}
        />
        <SidebarItem
          name={t('Workflows')}
          icon="LightningBoltIcon"
          href={workflowUrl}
          isExpanded={isExpanded}
          isSelected={pathname.startsWith("/w/") || pathname === "/workflows"}
        />
        <SidebarItem
          name={t('Signatures')}
          icon="PencilAltIcon"
          href={"/signatures"}
          isExpanded={isExpanded}
          isSelected={pathname.startsWith("/signatures")}
          isHidden
        />
        <SidebarItem
          name={t('Outputs')}
          icon="FolderOpenIcon"
          href={"/outputs"}
          isExpanded={isExpanded}
          isSelected={pathname.startsWith("/outputs")}
        />
        <SidebarItem
          name={t('Generate')}
          icon="SparklesIcon"
          href={"/generate"}
          isExpanded={isExpanded}
          isSelected={pathname.startsWith("/generate")}
          isHidden
        />
      </div>
      <div className='mt-auto' />
      <div className='flex flex-col gap-2'>
        <SidebarItem
          name={t('Referals & Credits')}
          icon="GiftIcon"
          href={"/settings/referrals"}
          isExpanded={isExpanded}
          isSelected={pathname === "/settings/referrals"}
        />
        <SidebarItem
          name={t('Guides')}
          icon="AcademicCapIcon"
          href={"https://docs.portant.co"}
          isExpanded={isExpanded}
          isSelected={false}
          isExternal
        />
        <SidebarItem
          name={t('Settings')}
          icon="CogIcon"
          href={settingsUrl}
          isExpanded={isExpanded}
          isSelected={pathname.startsWith("/settings") && pathname !== "/settings/referrals"}
        />
      </div>
      <div className="flex flex-col  justify-center font-gilroy font-semibold text-xs text-gray-300 pl-4 h-16 mr-auto">
        <div>v{packageJson?.version}</div>
        <div>(v{apiVersionData?.version ?? "---"})</div>
      </div>
    </nav>

  );
}

export default Sidebar;
